import { type Subject, type TimeEntryType } from '@ast-data/shared';

/**
 * The two Zeiterfassungen — Mitarbeiter (working hours) and Geräte (machine
 * operating hours). They share ONE RxDB collection, discriminated by
 * `entryType`, and therefore share every screen: the office grid, the form
 * dialog and the Feldaufnahme day view all take a variant and read their labels,
 * routes and person-only rules from it rather than branching on the type.
 *
 * `workerName` carries the Gerät's name on an equipment row — same column, same
 * autocomplete, only a different label.
 */
export interface TimeEntryVariant {
    /** The discriminator stored on every row this variant creates. */
    type: TimeEntryType;
    /**
     * The screen right the office list sits behind. Read where something offers
     * a way INTO that list — the conflict dialog's link to the editor — so it
     * is offered only to someone the route would actually let in.
     */
    accessSubject: Subject;
    /** Menu entry + page heading — the two read identically by design. */
    labelId: string;
    /**
     * Office route. The field routes are not here: their segment doubles as the
     * `FieldCategoryKey` union, so it is declared in fieldCategories.tsx.
     */
    officePath: string;
    addId: string;
    /** Label of the name column/field: "Mitarbeiter" or "Gerät". */
    nameLabelId: string;
    deleteTitleId: string;
    deleteConfirmId: string;
    newTitleId: string;
    editTitleId: string;
    historyTitleId: string;
    overlapErrorId: string;
    /**
     * The sentence under the conflict panel's table, saying what a
     * double-booking means here — a person cannot be in two places at once, a
     * Gerät cannot be on two sites.
     */
    conflictHelpId: string;
    /**
     * Whether the break column/field is offered. Machines take none, so an
     * equipment row's pauseMinutes stays 0 and its net hours are the full span.
     */
    showPause: boolean;
    /**
     * Whether the Anzahl column/field is offered. A Gerät is booked by the
     * piece — three of one kind for the same span is ONE row of three — and the
     * hours then count once per piece (2 Bagger × 8:00 = 16:00). A person is
     * always one, so a worker row keeps the quantity of 1 that makes the same
     * multiplication a no-op.
     */
    showQuantity: boolean;
    /**
     * Whether the form offers "ganzer Tag" beside Von/Bis. A Gerät often stands
     * on site round the clock; a person's day always has real times.
     */
    showAllDay: boolean;
    /**
     * Whether the name is PICKED from the app's active accounts instead of
     * typed. Mitarbeiter are people the app already knows, so the field offers
     * exactly them — and then several at once, because a whole crew works the
     * same span and each of them becomes an entry of its own. A Gerät has no
     * account to pick from: it is named freely the first time it is booked and
     * only suggested from the recorded names afterwards.
     */
    namesFromAccounts: boolean;
    /**
     * Whether the name resolves to an app user — the account link (`userId`,
     * which also drives overlap identity) and the user names among the
     * autocomplete suggestions. Geräte are not people, so neither applies.
     */
    linkUsers: boolean;
    /**
     * Leading part of the week-PDF's file name, before the KWs it covers
     * ("Zeiterfassung_Mitarbeiter_KW25-2026.pdf"). It names WHICH Zeiterfassung,
     * because the same weeks are exported from both. Deliberately German and NOT
     * translated:
     * an exported file keeps its name once it is filed away, so it must not
     * depend on the UI language it happened to be exported in.
     */
    exportFileStem: string;
}
/**
 * NB: no flag for the ArbZG break/rest warnings — they are entitlements of
 * people, and `computeTimeEntryWarnings` skips equipment rows itself, so an
 * equipment row simply never carries one.
 */

export const WORKER_TIME_ENTRY_VARIANT: TimeEntryVariant = {
    type: 'worker',
    accessSubject: 'WorkerTimeTracking',
    labelId: 'nav.timeEntriesWorkers',
    officePath: '/office/time-entries',
    addId: 'timeEntry.add',
    nameLabelId: 'timeEntry.field.workerName',
    deleteTitleId: 'timeEntry.delete.title',
    deleteConfirmId: 'timeEntry.delete.confirm',
    newTitleId: 'timeEntry.form.newTitle',
    editTitleId: 'timeEntry.form.editTitle',
    historyTitleId: 'timeEntry.history.title',
    overlapErrorId: 'timeEntry.overlapError',
    conflictHelpId: 'timeEntry.conflict.help',
    showPause: true,
    showQuantity: false,
    showAllDay: false,
    namesFromAccounts: true,
    linkUsers: true,
    exportFileStem: 'Zeiterfassung_Mitarbeiter'
};

export const EQUIPMENT_TIME_ENTRY_VARIANT: TimeEntryVariant = {
    type: 'equipment',
    accessSubject: 'EquipmentTimeTracking',
    labelId: 'nav.timeEntriesEquipment',
    officePath: '/office/equipment-time-entries',
    addId: 'equipmentEntry.add',
    nameLabelId: 'equipmentEntry.field.name',
    deleteTitleId: 'equipmentEntry.delete.title',
    deleteConfirmId: 'equipmentEntry.delete.confirm',
    newTitleId: 'equipmentEntry.form.newTitle',
    editTitleId: 'equipmentEntry.form.editTitle',
    historyTitleId: 'equipmentEntry.history.title',
    overlapErrorId: 'equipmentEntry.overlapError',
    conflictHelpId: 'equipmentEntry.conflict.help',
    showPause: false,
    showQuantity: true,
    showAllDay: true,
    namesFromAccounts: false,
    linkUsers: false,
    exportFileStem: 'Zeiterfassung_Geräte'
};

/** The variant a stored row belongs to, for code that holds only the row. */
export function timeEntryVariantOf(type: TimeEntryType): TimeEntryVariant {
    return type === EQUIPMENT_TIME_ENTRY_VARIANT.type
        ? EQUIPMENT_TIME_ENTRY_VARIANT
        : WORKER_TIME_ENTRY_VARIANT;
}

/**
 * The i18n id labelling a schema field of this variant — used where fields are
 * labelled generically from the entity (the Verlauf dialog), not by a hand-built
 * column. Only `workerName` differs between the two (Mitarbeiter / Gerät);
 * everything else shares the `timeEntry.field.*` catalog.
 */
export function timeEntryFieldLabelId(
    variant: TimeEntryVariant,
    field: string
): string {
    return field === 'workerName'
        ? variant.nameLabelId
        : `timeEntry.field.${field}`;
}
